import { Typography } from '@mui/material';
import { differenceInCalendarDays } from 'date-fns';
import {
  HourStatus,
  OperatingTimespansForDay,
} from 'features/locations/location.types';
import { getCurrentHourDetails } from 'features/locations/utils/operatingHours';
import { TFunction } from 'i18next';
import { ReactNode } from 'react';
import { Trans } from 'react-i18next';
import { formatDate } from 'shared/dates';

type CurrentHourStatus = {
  status: HourStatus;
  statusLabel: string;
  label: ReactNode;
};

const STATUS_TRANS_KEYS: Record<HourStatus, string> = {
  open: 'location.hourStatus.open',
  closed: 'location.hourStatus.closed',
  closingSoon: 'location.hourStatus.closingSoon',
};

const TIME_FORMAT = 'h:mm a'; // "9:00 AM"

const buildClosesAtLabel = (
  closeDate: Date,
  referenceDate: Date,
): ReactNode => {
  const time = formatDate(closeDate, TIME_FORMAT);
  // For an active weekly slot, the close instant is at most the next local
  // calendar day after `referenceDate` (same-day hours or overnight into tomorrow).
  const daysUntilClose = differenceInCalendarDays(closeDate, referenceDate);

  if (daysUntilClose <= 0) {
    return (
      <Typography component="span">
        <Trans i18nKey="location.closesAt" values={{ time }}>
          Closes at&nbsp;
          <b style={{ textTransform: 'uppercase' }}>{time}</b>
        </Trans>
      </Typography>
    );
  }

  return (
    <Typography component="span">
      <Trans i18nKey="location.closesTomorrowAt" values={{ time }}>
        Closes tomorrow at&nbsp;
        <b style={{ textTransform: 'uppercase' }}>{time}</b>
      </Trans>
    </Typography>
  );
};

/**
 * Renders one of three variants depending on how far in the future the next
 * opening is: later today, tomorrow, or on a named weekday.
 */
const buildOpensAtLabel = (
  nextOpenDate: Date,
  referenceDate: Date,
): ReactNode => {
  const time = formatDate(nextOpenDate, TIME_FORMAT);
  const daysAhead = differenceInCalendarDays(nextOpenDate, referenceDate);

  if (daysAhead <= 0) {
    return (
      <Typography component="span">
        <Trans i18nKey="location.opensAt" values={{ time }}>
          Opens at <b>{time}</b>
        </Trans>
      </Typography>
    );
  }

  if (daysAhead === 1) {
    return (
      <Typography component="span">
        <Trans i18nKey="location.opensTomorrowAt" values={{ time }}>
          Opens tomorrow at <b>{time}</b>
        </Trans>
      </Typography>
    );
  }

  const day = formatDate(nextOpenDate, 'eeee'); // "Monday", "Tuesday", …
  return (
    <Typography component="span">
      <Trans i18nKey="location.opensOnDayAt" values={{ day, time }}>
        Opens next {day} at <b>{time}</b>
      </Trans>
    </Typography>
  );
};

const buildStatus = (
  t: TFunction,
  status: HourStatus,
  label: ReactNode,
): CurrentHourStatus => ({
  status,
  statusLabel: t(STATUS_TRANS_KEYS[status]),
  label,
});

/**
 * Builds the `(status chip, descriptive label)` pair shown in the location
 * details header. Pass raw `hours` and the location IANA zone; viewer
 * timezone and `now` are resolved inside `getCurrentHourDetails`.
 */
export const getCurrentHourStatus = (
  t: TFunction,
  hours: OperatingTimespansForDay[],
  locationTimeZone: string | undefined,
): CurrentHourStatus => {
  const { status, closeAt, nextOpenAt, now } = getCurrentHourDetails(
    hours,
    locationTimeZone,
  );

  if (closeAt) {
    return buildStatus(t, status, buildClosesAtLabel(closeAt, now));
  }
  if (nextOpenAt) {
    return buildStatus(t, 'closed', buildOpensAtLabel(nextOpenAt, now));
  }

  return buildStatus(t, 'closed', null);
};
